import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { parentApi, type ParentLinkStudent } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { usePageTitle } from '@/hooks/usePageTitle';
import LoadingSpinner from '@/components/LoadingSpinner';
import { FeatureModuleGuard } from '@/components/FeatureModuleGuard';
import { PhoneNumberInput } from '@/components/forms/PhoneNumberInput';
import { SchoolLogo } from '@/components/SchoolLogo';
import { formatApiError } from '@/utils/errorClassifier';
import { formatGradeI18n } from '@/utils/gradeLabels';
import { LanguageToggle } from '@/components/parent/LanguageToggle';

export default function ParentRegistration() {
  const { t, i18n } = useTranslation(['parent', 'common']);
  usePageTitle(t('registration.pageTitle'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenant, user, isAuthenticated } = useAuthStore();

  const rawUrlCode = searchParams.get('code');
  const urlCode = rawUrlCode ? rawUrlCode.toUpperCase().replace(/[^A-F0-9]/g, '').slice(0, 6) : null;
  const [manualCode, setManualCode] = useState('');
  const [studentInfo, setStudentInfo] = useState<ParentLinkStudent | null>(null);
  const [validating, setValidating] = useState(!!urlCode); // Only validating if URL has code
  const [error, setError] = useState<string | null>(null);

  // The active code is either from URL or manually entered
  const activeCode = urlCode || manualCode;

  // Form state
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [relationship, setRelationship] = useState('mother');
  const [registrationComplete, setRegistrationComplete] = useState(false);

  // Validate URL code on mount (only if code is in URL)
  useEffect(() => {
    if (!urlCode || !tenant) {
      setValidating(false);
      return;
    }

    setValidating(true);
    setError(null);

    parentApi
      .validateLinkCode(urlCode, tenant.id)
      .then((response) => {
        if (response.data.valid) {
          setStudentInfo(response.data.student || null);
        } else {
          setError(response.data.error || t('registration.invalidLinkCode'));
        }
      })
      .catch(() => {
        setError(t('registration.failedToValidate'));
      })
      .finally(() => {
        setValidating(false);
      });
  }, [urlCode, tenant, t]);

  // Validate manually entered code
  const validateManualCode = async () => {
    if (!manualCode || !tenant) return;

    setValidating(true);
    setError(null);

    try {
      const response = await parentApi.validateLinkCode(manualCode, tenant.id);
      if (response.data.valid) {
        setStudentInfo(response.data.student || null);
      } else {
        setError(response.data.error || t('registration.invalidLinkCode'));
      }
    } catch {
      setError(t('registration.failedToValidate'));
    } finally {
      setValidating(false);
    }
  };

  const registerMutation = useMutation({
    mutationFn: async (formData: {
      linkCode: string;
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      relationship: string;
      language?: string;
    }) => {
      return parentApi.register(formData);
    },
    onSuccess: (response) => {
      if (response?.requiresEmailVerification) {
        setRegistrationComplete(true);
      } else {
        navigate('/login?message=registration_complete');
      }
    },
    onError: (error: unknown) => {
      setError(formatApiError(error));
    },
  });

  const linkChildMutation = useMutation({
    mutationFn: async (formData: { linkCode: string; relationship: string }) => {
      return parentApi.linkChild(formData);
    },
    onSuccess: (response) => {
      if (response?.alreadyLinked) {
        setError(response.message);
      } else {
        navigate('/');
      }
    },
    onError: (error: unknown) => {
      setError(formatApiError(error));
    },
  });

  const handleLinkChild = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeCode) return;
    setError(null);
    linkChildMutation.mutate({ linkCode: activeCode, relationship });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeCode) return;
    setError(null);

    if (email !== confirmEmail) {
      setError(t('registration.emailsDoNotMatch'));
      return;
    }

    registerMutation.mutate({
      linkCode: activeCode,
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      relationship,
      language: i18n.language?.startsWith('es') ? 'es' : 'en',
    });
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '500px',
    margin: '1rem auto',
    padding: '1.5rem',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border-color)',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.75rem',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '36px',
    width: '100%',
  };

  if (registrationComplete) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          {tenant?.settings.branding.logoFileId && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
            </div>
          )}
          <h1 style={{ lineHeight: '1.2', marginBottom: '1rem' }}>{t('registration.welcomeTitle')}</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            <Trans
              t={t}
              i18nKey="registration.welcomeEmailSent"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            {t('registration.verifyEmailInstructions')}
          </p>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem', marginBottom: '2rem' }}>
            {t('registration.checkSpam')}
          </p>
          <a
            href="/login"
            style={{
              ...buttonStyle,
              display: 'inline-block',
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            {t('registration.goToLogin')}
          </a>
        </div>
      </div>
    );
  }

  if (validating) {
    return <LoadingSpinner />;
  }

  // Show code entry form if no valid student info yet
  if (!studentInfo) {
    return (
      <FeatureModuleGuard
        module="parentPortal"
        fallback={
          <div style={containerStyle}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>{t('registration.portalUnavailable')}</p>
            </div>
          </div>
        }
      >
        <div style={containerStyle}>
          <LanguageToggle />
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            {/* School Logo (if uploaded) */}
            {tenant?.settings.branding.logoFileId && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
              </div>
            )}
            <h1 style={{ lineHeight: '1.2' }}>{t('registration.title')}</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{tenant?.schoolName}</p>
          </div>

          {error && (
            <div
              style={{
                padding: '1rem',
                backgroundColor: 'var(--error-bg)',
                color: 'var(--error-text)',
                borderRadius: 'var(--radius)',
                marginBottom: '1rem',
              }}
            >
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          )}

          <div className="form-group">
            <label>{t('registration.enterCode')}</label>
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase().replace(/[^A-F0-9]/g, '').slice(0, 6))}
              placeholder={t('registration.codePlaceholder')}
              className="form-input"
              style={{
                textAlign: 'center',
                fontSize: '1.5rem',
                letterSpacing: '0.2em',
                fontFamily: 'monospace',
              }}
              maxLength={6}
            />
            <p className="help-text">{t('registration.codeHelpText')}</p>
          </div>

          <button onClick={validateManualCode} disabled={manualCode.length !== 6} style={buttonStyle}>
            {t('registration.continue')}
          </button>

          <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem' }}>
            {t('common:auth.alreadyHaveAccount')}{' '}
            <a href={activeCode ? `/login?linkCode=${activeCode}` : '/login'} style={{ color: 'var(--primary-color)' }}>
              {t('common:auth.logIn')}
            </a>
          </p>

          <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
            {t('registration.noCode')}
          </p>
        </div>
      </FeatureModuleGuard>
    );
  }

  // Authenticated parent linking a new child (sibling)
  if (isAuthenticated && user?.role === 'parent' && studentInfo) {
    return (
      <FeatureModuleGuard
        module="parentPortal"
        fallback={
          <div style={containerStyle}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>{t('registration.portalUnavailable')}</p>
            </div>
          </div>
        }
      >
        <div style={containerStyle}>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            {tenant?.settings.branding.logoFileId && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
              </div>
            )}
            <h1 style={{ lineHeight: '1.2' }}>{t('registration.linkStudentTitle')}</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{tenant?.schoolName}</p>
          </div>

          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.1rem' }}>
              {t('registration.linkingStudent')}
            </p>
            <h2 style={{ margin: '0.1rem 0', fontSize: '1.5rem' }}>
              {studentInfo.firstName} {studentInfo.lastName}
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{formatGradeI18n(studentInfo.grade, t)}</p>
          </div>

          <div
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              <Trans
                t={t}
                i18nKey="registration.loggedInAs"
                values={{ email: user.email }}
                components={{ strong: <strong /> }}
              />
            </p>
          </div>

          {error && (
            <div
              style={{
                padding: '1rem',
                backgroundColor: 'var(--error-bg)',
                color: 'var(--error-text)',
                borderRadius: 'var(--radius)',
                marginBottom: '1rem',
              }}
            >
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleLinkChild} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
            <div className="form-group">
              <label>
                {t('common:form.relationship')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <select
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                required
                className="form-input"
              >
                <option value="mother">{t('common:relationships.mother')}</option>
                <option value="father">{t('common:relationships.father')}</option>
                <option value="guardian">{t('common:relationships.guardian')}</option>
                <option value="grandparent">{t('common:relationships.grandparent')}</option>
                <option value="stepparent">{t('common:relationships.stepparent')}</option>
                <option value="self">{t('common:relationships.self')}</option>
                <option value="other">{t('common:relationships.other')}</option>
              </select>
            </div>

            <button type="submit" disabled={linkChildMutation.isPending} style={buttonStyle}>
              {linkChildMutation.isPending ? t('registration.linkingStudentPending') : t('registration.linkStudentButton')}
            </button>
          </form>
        </div>
      </FeatureModuleGuard>
    );
  }

  return (
    <FeatureModuleGuard
      module="parentPortal"
      fallback={
        <div style={containerStyle}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{t('registration.portalUnavailable')}</p>
          </div>
        </div>
      }
    >
      <div style={containerStyle}>
        <LanguageToggle />
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          {/* School Logo (if uploaded) */}
          {tenant?.settings.branding.logoFileId && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
            </div>
          )}
          <h1 style={{ lineHeight: '1.2' }}>{t('registration.title')}</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{tenant?.schoolName}</p>
        </div>

        {studentInfo && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.1rem' }}>
              {t('registration.registeringFor')}
            </p>
            <h2 style={{ margin: '0.1rem 0', fontSize: '1.5rem' }}>
              {studentInfo.firstName} {studentInfo.lastName}
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{formatGradeI18n(studentInfo.grade, t)}</p>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error-text)',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
            }}
          >
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          <div className="form-group">
            <label>
              {t('common:form.emailAddress')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>
              {t('common:form.confirmEmailAddress')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>
              {t('common:form.password')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="form-input"
            />
            <p className="help-text" style={{ fontSize: '0.7rem' }}>
              {t('registration.passwordMinLength')}
            </p>
          </div>

          <div className="form-group">
            <label>
              {t('common:form.firstName')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>
              {t('common:form.lastName')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{t('common:form.phoneNumber')}</label>
            <PhoneNumberInput value={phoneNumber} onChange={setPhoneNumber} placeholder="(555) 555-5555" />
            <p className="help-text" style={{ fontSize: '0.7rem' }}>
              {t('registration.phoneHelpText')}{' '}
              <Trans
                t={t}
                i18nKey="registration.seeSmSTerms"
                components={{
                  smsTermsLink: <a href="/sms-terms" style={{ color: 'var(--primary-color)' }} />,
                }}
              />
            </p>
          </div>

          <div className="form-group">
            <label>
              {t('common:form.relationship')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <select
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              required
              className="form-input"
            >
              <option value="mother">{t('common:relationships.mother')}</option>
              <option value="father">{t('common:relationships.father')}</option>
              <option value="guardian">{t('common:relationships.guardian')}</option>
              <option value="grandparent">{t('common:relationships.grandparent')}</option>
              <option value="stepparent">{t('common:relationships.stepparent')}</option>
              <option value="self">{t('common:relationships.self')}</option>
              <option value="other">{t('common:relationships.other')}</option>
            </select>
          </div>

          <button type="submit" disabled={registerMutation.isPending} style={buttonStyle}>
            {registerMutation.isPending ? t('registration.creatingAccount') : t('registration.createAccount')}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem' }}>
          {t('common:auth.alreadyHaveAccount')}{' '}
          <a href={`/login?linkCode=${activeCode}`} style={{ color: 'var(--primary-color)' }}>
            {t('common:auth.logIn')}
          </a>
        </p>
      </div>
    </FeatureModuleGuard>
  );
}
